import { supabase, getCachedAccessToken } from '@lib/supabaseClient';
import { useCallback } from 'react';
import { useLogger } from '@providers/loggerProvider';
import { isAppAuthFailure, useAuth } from '@providers/authProvider';
import { fetchWithTimeout, apiRequestWithRetry } from '@utils/networkUtils';
import { getApiBaseUrl } from '@utils/apiUrl';
import { recordBricklinkApiMetadata } from '@utils/bricklinkQuotaStore';

// The API answers with this code when the Supabase access token it validated
// expired server-side (api/middleware/authErrors.js EXPIRED_TOKEN).
const EXPIRED_ACCESS_TOKEN_CODE = 'API_ACCESS_TOKEN_EXPIRED';

// Single-flight session refresh. Many requests can hit an expired token at the
// same moment (page loads after an idle hour), and every one of them must share
// the one refresh instead of racing Supabase's refresh-token rotation.
let _activeSessionRefresh = null;
const refreshSessionOnce = () => {
    if (!_activeSessionRefresh) {
        _activeSessionRefresh = supabase.auth.refreshSession()
            .then(({ data: { session } }) => session?.access_token || null)
            .catch(() => null)
            .finally(() => {
                _activeSessionRefresh = null;
            });
    }
    return _activeSessionRefresh;
};


const buildRequestOptions = ({ method, body, headers, isDevelopment }) => {
    const options = {
        method,
        headers,
        cache: 'no-store', // Disable browser HTTP caching to prevent body stream consumption issues
        ...(isDevelopment && { credentials: 'include' })
    };

    if (body) {
        const isFormData = body instanceof FormData;
        options.body = isFormData ? body : JSON.stringify(body);
    }

    return options;
};

const validateRequestParams = ({ endpoint, method, body, customConfig }) => {
    if (!endpoint || typeof endpoint !== 'string') {
        throw new Error('Endpoint must be a non-empty string');
    }

    const validMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
    if (!validMethods.includes(method)) {
        throw new Error(`Invalid HTTP method: ${method}. Must be one of: ${validMethods.join(', ')}`);
    }

    if (body !== null && !(body instanceof FormData) && typeof body !== 'object') {
        throw new Error('Request body must be null, an object, or FormData');
    }

    if (customConfig !== null && typeof customConfig !== 'object') {
        throw new Error('customConfig must be null or an object');
    }

    if (customConfig?.params && typeof customConfig.params !== 'object') {
        throw new Error('customConfig.params must be an object');
    }

    if (customConfig?.headers && typeof customConfig.headers !== 'object') {
        throw new Error('customConfig.headers must be an object');
    }
};

export const useApi = () => {
    const logger = useLogger('useApi');
    const { endSession } = useAuth();

    const handleNetworkError = useCallback((error) => {
        if (error.code === 'REQUEST_TIMEOUT' || error.message?.includes('Request timeout')) {
            const timeoutError = new Error(`Request timed out after ${error.timeout || 30000}ms. Please try again.`);
            timeoutError.originalError = error;
            timeoutError.code = 'REQUEST_TIMEOUT';
            timeoutError.timeout = error.timeout;
            logger.error('Request timeout error', timeoutError);
            return timeoutError;
        }

        if (error.message === 'Failed to fetch' ||
            error.message?.includes('connect to the server') ||
            error.message?.includes('net::ERR_CONNECTION_REFUSED')) {
            const enhancedError = new Error('Unable to connect to the server. Please check your internet connection and try again.');
            enhancedError.originalError = error;
            enhancedError.code = 'CONNECTION_ERROR';
            logger.error('Network connection error', enhancedError);
            return enhancedError;
        }

        return error;
    }, [logger]);

    const buildHeaders = useCallback(({ body, customHeaders, accessToken }) => {
        const headers = {
            ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' }),
            ...customHeaders,
        };

        if (!customHeaders?.Authorization && accessToken) {
            if (typeof accessToken !== 'string' || !accessToken.trim()) {
                logger.error('Invalid access token format');
                throw new Error('Invalid access token format');
            }
            headers['Authorization'] = `Bearer ${accessToken.trim()}`;
        }

        return headers;
    }, [logger]);

    const processResponse = useCallback(async ({ response, returnFullResponse, accessToken }) => {
        // Clone the response to avoid "body stream already read" errors from browser cache
        const responseToRead = response.clone();
        let jsonResponse;

        try {
            jsonResponse = await responseToRead.json();
        } catch (parseError) {
            logger.error('Failed to parse JSON response', {
                status: response.status,
                url: response.url,
                error: parseError.message
            });
            throw new Error(`Failed to parse response as JSON: ${parseError.message}`);
        }

        // The API attaches bricklink_api_metadata to every response, success or
        // failure. Record it here, the single funnel every response passes
        // through, so the header learns about a quota overage without each
        // service hook threading the block back to the UI.
        recordBricklinkApiMetadata(jsonResponse.bricklink_api_metadata);

        const responseWithMeta = {
            ...jsonResponse,
            headers: response.headers,
            status: response.status
        };

        if (!jsonResponse.success) {
            const errorResponse = {
                ...responseWithMeta,
                meta: {
                    ...responseWithMeta.meta,
                    error: responseWithMeta.meta?.error || {
                        code: responseWithMeta.error?.code || 'API_ERROR',
                        message: responseWithMeta.error?.message || 'API request failed'
                    }
                }
            };

            logger.error('API response error', {
                status: response.status,
                endpoint: response.url,
                message: errorResponse.meta?.error?.message
            });

            if (returnFullResponse && errorResponse.meta.error.code !== EXPIRED_ACCESS_TOKEN_CODE) {
                if (isAppAuthFailure(errorResponse) && accessToken) void endSession(accessToken);
                return errorResponse;
            }
            throw errorResponse;
        }

        return returnFullResponse ? responseWithMeta : jsonResponse;
    }, [logger, endSession]);

    const apiRequest = useCallback(async (endpoint, method = 'GET', body = null, customConfig = {}, returnFullResponse = false) => {
        let requestAccessToken = null;
        try {
            validateRequestParams({ endpoint, method, body, customConfig });

            const apiBase = '/api';
            const apiEndpoint = endpoint.startsWith('/api')
                ? endpoint
                : `${apiBase}${endpoint}`;
            let url = `${getApiBaseUrl()}${apiEndpoint}`;

            // URLSearchParams stringifies undefined values as the literal
            // "undefined", so drop those params instead of sending them.
            const definedParams = Object.entries(customConfig?.params || {})
                .filter(([, value]) => value !== undefined);
            if (definedParams.length > 0) {
                const queryString = new URLSearchParams(definedParams).toString();
                url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
            }

            const executeRequest = async (accessToken) => {
                const headers = buildHeaders({
                    body,
                    customHeaders: customConfig?.headers,
                    accessToken
                });
                requestAccessToken = typeof headers.Authorization === 'string'
                    ? headers.Authorization.replace(/^Bearer /, '')
                    : null;

                const options = buildRequestOptions({
                    method,
                    body,
                    headers,
                    isDevelopment: process.env.REACT_APP_BB_ENV === 'dev_test'
                });

                let response;
                try {
                    response = await fetchWithTimeout(url, options, customConfig?.timeout || 30000);
                } catch (fetchError) {
                    throw handleNetworkError(fetchError);
                }

                return await processResponse({ response, returnFullResponse, accessToken: requestAccessToken });
            };

            // Use cached token to avoid lock contention from concurrent getSession() calls
            let accessToken = getCachedAccessToken();
            if (!accessToken) {
                const { data: { session } } = await supabase.auth.getSession();
                accessToken = session?.access_token;
            }
            if (!accessToken) throw new Error('Not authenticated');

            try {
                return await executeRequest(accessToken);
            } catch (error) {
                // The cached token expired server-side (e.g. the tab sat idle
                // past the Supabase access-token lifetime and the background
                // refresh was missed or lost the rotation race). Refresh the
                // session once and retry the request; the retry itself is
                // never retried again.
                if (error?.meta?.error?.code !== EXPIRED_ACCESS_TOKEN_CODE) {
                    throw error;
                }
                if (requestAccessToken !== getCachedAccessToken()) throw error;
                const refreshedAccessToken = await refreshSessionOnce();
                if (!refreshedAccessToken) {
                    throw error;
                }
                return await executeRequest(refreshedAccessToken);
            }

        } catch (error) {
            if (isAppAuthFailure(error)) {
                if (requestAccessToken) void endSession(requestAccessToken);
                if (returnFullResponse) return error;
            }
            logger.error('API request failed', {
                endpoint,
                method,
                message: error?.message
            });
            if (error.message === 'Failed to fetch' || error.code === 'CONNECTION_ERROR') {
                throw handleNetworkError(error);
            }

            throw error;
        }
    }, [buildHeaders, handleNetworkError, logger, processResponse, endSession]);

    // Enhanced API request with retry logic
    const apiRequestWithRetrySupport = useCallback(async (
        endpoint,
        method = 'GET',
        body = null,
        customConfig = {},
        returnFullResponse = false,
        retryOptions = {}
    ) => {
        const {
            enableRetry = true,
            _maxRetries = 3,
            timeoutMs = 30000
        } = retryOptions;

        if (!enableRetry) {
            // Use original API request if retry is disabled
            const configWithTimeout = { ...customConfig, timeout: timeoutMs };
            return apiRequest(endpoint, method, body, configWithTimeout, returnFullResponse);
        }

        // Use enhanced retry logic
        return apiRequestWithRetry(
            apiRequest,
            endpoint,
            method,
            body,
            customConfig,
            returnFullResponse,
            {
                timeout: timeoutMs,
                enableRetry: true
            }
        );
    }, [apiRequest]);

    return {
        apiRequest,
        apiRequestWithRetry: apiRequestWithRetrySupport
    };
};
